<template>
  <div class="flex flex-col gap-4">
    <div class="inline-flex w-fit rounded-md border bg-muted p-0.5" role="group" aria-label="Programmes affichés">
      <button
        v-for="option in FILTERS"
        :key="option.value"
        type="button"
        :aria-pressed="filter === option.value"
        class="h-8 rounded-sm px-3 text-sm font-medium text-muted-foreground"
        :class="filter === option.value ? 'bg-background text-foreground shadow-xs' : ''"
        @click="filter = option.value"
      >
        {{ option.label }} <span class="tabular-nums">({{ countOf(option.value) }})</span>
      </button>
    </div>

    <!-- The figures follow the programs shown below: the filter applies to both. -->
    <div class="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      <Card v-for="kpi in kpis" :key="kpi.label" class="gap-0 py-4 sm:py-6">
        <CardHeader class="px-4 sm:px-6">
          <CardDescription>{{ kpi.label }}</CardDescription>
          <CardTitle class="text-2xl tabular-nums sm:text-3xl">{{ kpi.value }}</CardTitle>
        </CardHeader>
      </Card>
    </div>

    <Card>
      <CardHeader class="px-4 sm:px-6">
        <div class="flex items-center gap-1">
          <CardTitle class="text-base">Programmes</CardTitle>
          <InfoHint v-if="chart.description" label="Programmes" :text="chart.description" />
        </div>
      </CardHeader>
      <CardContent class="flex flex-col gap-3 px-4 sm:px-6">
        <!-- On a phone, one block per program: a seven-column table would only show its first two columns. -->
        <ul class="flex flex-col gap-2 md:hidden">
          <li v-for="program in shown" :key="program.id">
            <button
              type="button"
              class="flex w-full flex-col gap-3 rounded-md border p-3 text-left hover:bg-accent"
              @click="emit('open', program.id)"
            >
              <span class="flex items-start justify-between gap-2">
                <span class="min-w-0">
                  <span class="block font-medium">{{ program.name }}</span>
                  <span v-if="program.dates" class="block text-xs text-muted-foreground">{{ program.dates }}</span>
                </span>
                <span class="shrink-0 rounded-sm px-2 py-0.5 text-xs font-medium" :class="STATUS_CLASS[program.status]">
                  {{ program.status }}
                </span>
              </span>
              <span v-if="program.objective" class="flex flex-col gap-1">
                <span v-if="program.objective.type" class="text-xs text-muted-foreground">{{
                  program.objective.type
                }}</span>
                <span v-if="program.objective.measure" class="flex items-center gap-3">
                  <span class="relative h-2 flex-1 rounded-full bg-background ring-1 ring-border" aria-hidden="true">
                    <span
                      class="absolute inset-y-0 left-0 rounded-full"
                      :class="barFill(program.objective.measure, LEVELS_ON)"
                      :style="{ width: `${shareOf(program.objective.measure)}%` }"
                    />
                  </span>
                  <span class="text-sm font-medium tabular-nums">{{ program.objective.value }}</span>
                </span>
                <span v-else class="text-sm font-medium text-muted-foreground">{{ program.objective.value }}</span>
                <HealthLevel v-if="program.objective.measure?.level" :level="program.objective.measure.level" />
                <span class="text-xs text-muted-foreground">{{ program.objective.unit }}</span>
                <span v-if="program.objective.excluded" class="text-xs text-muted-foreground">{{
                  program.objective.excluded
                }}</span>
              </span>
              <span v-else-if="program.done !== null" class="flex items-center gap-3">
                <span class="relative h-2 flex-1 rounded-full bg-background ring-1 ring-border">
                  <span
                    class="absolute inset-y-0 left-0 rounded-full bg-primary"
                    :style="{ width: `${program.done}%` }"
                  />
                  <span
                    v-if="program.elapsed !== null"
                    class="absolute -top-1 h-4 w-0.5 bg-foreground"
                    :style="{ left: `${program.elapsed}%` }"
                  />
                </span>
                <span class="text-sm font-medium tabular-nums">{{ Math.round(program.done) }} %</span>
              </span>
              <span class="flex gap-4 text-sm text-muted-foreground">
                <span
                  ><strong class="font-medium text-foreground tabular-nums">{{
                    program.binomes.toLocaleString('fr-FR')
                  }}</strong>
                  groupes</span
                >
                <span
                  ><strong class="font-medium text-foreground tabular-nums">{{
                    program.sessions.toLocaleString('fr-FR')
                  }}</strong>
                  sessions</span
                >
                <span
                  >note
                  <strong class="font-medium text-foreground tabular-nums">{{ formatNote(program.note) }}</strong></span
                >
              </span>
            </button>
          </li>
          <li v-if="shown.length === 0" class="py-6 text-center text-sm text-muted-foreground">Aucun programme</li>
        </ul>
        <div class="hidden md:block [&>[data-slot=table-container]]:max-h-[32rem]">
          <Table>
            <TableHeader class="sticky top-0 z-10 bg-muted shadow-[inset_0_-1px_0_var(--border)]">
              <TableRow class="border-b-0 hover:bg-transparent">
                <TableHead class="text-xs font-semibold text-muted-foreground">Programme</TableHead>
                <TableHead class="text-xs font-semibold text-muted-foreground">Statut</TableHead>
                <TableHead class="text-right text-xs font-semibold text-muted-foreground">Groupes</TableHead>
                <TableHead class="text-right text-xs font-semibold text-muted-foreground">Sessions</TableHead>
                <TableHead v-if="hasObjective" class="min-w-72 text-xs font-semibold text-muted-foreground"
                  >Objectif du programme</TableHead
                >
                <TableHead v-else class="min-w-48 text-xs font-semibold text-muted-foreground">Avancement</TableHead>
                <TableHead class="text-right text-xs font-semibold text-muted-foreground">Note</TableHead>
                <TableHead class="w-8"><span class="sr-only">Ouvrir</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow
                v-for="program in shown"
                :key="program.id"
                class="cursor-pointer border-b-0 even:bg-muted/60 hover:bg-accent"
                @click="emit('open', program.id)"
              >
                <TableCell class="py-3">
                  <button
                    type="button"
                    class="text-left font-medium hover:underline focus-visible:underline"
                    @click.stop="emit('open', program.id)"
                  >
                    {{ program.name }}
                  </button>
                  <div v-if="program.dates" class="text-xs text-muted-foreground">{{ program.dates }}</div>
                </TableCell>
                <TableCell>
                  <span
                    class="whitespace-nowrap rounded-sm px-2 py-0.5 text-xs font-medium"
                    :class="STATUS_CLASS[program.status]"
                  >
                    {{ program.status }}
                  </span>
                </TableCell>
                <TableCell class="text-right tabular-nums">{{ program.binomes.toLocaleString('fr-FR') }}</TableCell>
                <TableCell class="text-right tabular-nums">{{ program.sessions.toLocaleString('fr-FR') }}</TableCell>
                <TableCell v-if="program.objective">
                  <div class="flex flex-col gap-1">
                    <span v-if="program.objective.type" class="text-xs text-muted-foreground">{{
                      program.objective.type
                    }}</span>
                    <div v-if="program.objective.measure" class="flex items-center gap-3">
                      <div class="relative h-2 flex-1 rounded-full bg-background ring-1 ring-border" aria-hidden="true">
                        <div
                          class="absolute inset-y-0 left-0 rounded-full"
                          :class="barFill(program.objective.measure, LEVELS_ON)"
                          :style="{ width: `${shareOf(program.objective.measure)}%` }"
                        />
                      </div>
                      <span class="whitespace-nowrap text-sm font-medium tabular-nums">{{
                        program.objective.value
                      }}</span>
                    </div>
                    <span v-else class="text-sm font-medium text-muted-foreground">{{ program.objective.value }}</span>
                    <HealthLevel v-if="program.objective.measure?.level" :level="program.objective.measure.level" />
                    <span class="text-xs text-muted-foreground">{{ program.objective.unit }}</span>
                    <span v-if="program.objective.excluded" class="text-xs text-muted-foreground">{{
                      program.objective.excluded
                    }}</span>
                  </div>
                </TableCell>
                <TableCell v-else>
                  <div v-if="program.done !== null" class="flex items-center gap-3">
                    <div class="relative h-2 flex-1 rounded-full bg-background ring-1 ring-border">
                      <div
                        class="absolute inset-y-0 left-0 rounded-full bg-primary"
                        :style="{ width: `${program.done}%` }"
                      />
                      <div
                        v-if="program.elapsed !== null"
                        class="absolute -top-1 h-4 w-0.5 bg-foreground"
                        :style="{ left: `${program.elapsed}%` }"
                        :title="`Temps écoulé : ${Math.round(program.elapsed)} %`"
                      />
                    </div>
                    <span class="w-10 text-right text-sm font-medium tabular-nums"
                      >{{ Math.round(program.done) }} %</span
                    >
                  </div>
                  <span v-else class="text-muted-foreground">—</span>
                </TableCell>
                <TableCell class="text-right tabular-nums">{{ formatNote(program.note) }}</TableCell>
                <TableCell class="text-muted-foreground"><ChevronRight class="size-4" aria-hidden="true" /></TableCell>
              </TableRow>
              <TableEmpty v-if="shown.length === 0" :colspan="7">Aucun programme</TableEmpty>
            </TableBody>
          </Table>
        </div>
        <p v-if="hasObjective" class="text-xs leading-relaxed text-muted-foreground">{{ OVERVIEW_LEGEND }}</p>
        <p v-else class="flex items-center gap-2 text-xs text-muted-foreground">
          <span aria-hidden="true" class="inline-block h-3 w-0.5 bg-foreground" />
          Trait : temps écoulé du programme. Avancement : sessions réalisées sur sessions prévues.
        </p>
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { ChevronRight } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { DashboardChartType } from '@/features/admin/analytics/api/dashboard'
import HealthLevel from '@/features/ui/pages/admin/dashboard/dashboard-content/health-level.vue'
import InfoHint from '@/features/ui/pages/admin/dashboard/dashboard-content/info-hint.vue'
import {
  barFill,
  OVERVIEW_LEGEND,
  overviewObjective,
  shareOf,
} from '@/features/ui/pages/admin/dashboard/dashboard-content/program-health'
import { LEVELS_ON } from '@/features/ui/pages/admin/dashboard/dashboard-content/program-health-levels'

const props = defineProps<{ chart: DashboardChartType }>()
const emit = defineEmits<{ open: [programId: string] }>()

type StatusType = 'En cours' | 'À venir' | 'Terminé'
const STATUS_CLASS: Record<StatusType, string> = {
  'En cours': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  'À venir': 'bg-accent text-accent-foreground',
  'Terminé': 'bg-muted text-muted-foreground',
}
const ORDER: Record<StatusType, number> = { 'En cours': 0, 'À venir': 1, 'Terminé': 2 }
// A score is shown from 5 reviews, as on the program page.
const MIN_REVIEWS = 5

const pct = (v: unknown) => (typeof v === 'number' ? Math.max(0, Math.min(100, v)) : null)
const num = (v: unknown) => (typeof v === 'number' ? v : 0)
const formatNote = (note: number | null) => (note === null ? '—' : note.toFixed(1).replace('.', ','))

// With the `objectif` column (program health), each program is measured against its objective instead of « Avancement ».
const hasObjective = computed(() => props.chart.columns.includes('objectif'))

// One row per program, in progress first, then the most recent.
const programs = computed(() =>
  props.chart.rows
    .map((row) => {
      const status = (['En cours', 'À venir', 'Terminé'] as const).find((s) => s === row.statut) ?? 'En cours'
      return {
        id: String(row.programme_id),
        name: String(row.programme_nom ?? '—'),
        dates: row.debut && row.fin ? `${row.debut} au ${row.fin}` : '',
        sort: String(row.debut_tri ?? ''),
        status,
        binomes: num(row.binomes),
        sessions: num(row.sessions),
        done: pct(row.avancement_pct),
        elapsed: pct(row.temps_ecoule_pct),
        note: typeof row.note === 'number' ? row.note : null,
        reviews: num(row.nb_avis),
        notesTotal: num(row.total_notes),
        objective: hasObjective.value ? overviewObjective(row, LEVELS_ON) : null,
      }
    })
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || b.sort.localeCompare(a.sort)),
)

const FILTERS = [
  { value: 'current', label: 'En cours' },
  { value: 'finished', label: 'Terminés' },
  { value: 'all', label: 'Tous' },
] as const
type FilterType = (typeof FILTERS)[number]['value']

const matches = (value: FilterType, status: StatusType) => {
  if (value === 'current') return status !== 'Terminé'
  if (value === 'finished') return status === 'Terminé'
  return true
}
const countOf = (value: FilterType) => programs.value.filter((p) => matches(value, p.status)).length

const filter = ref<FilterType>(countOf('current') > 0 ? 'current' : 'all')
const shown = computed(() => programs.value.filter((p) => matches(filter.value, p.status)))

const PROGRAMS_LABEL: Record<FilterType, string> = {
  current: 'Programmes en cours',
  finished: 'Programmes terminés',
  all: 'Programmes',
}

// Totals of the programs shown; the score is weighted by the reviews of each program.
const kpis = computed(() => {
  const list = shown.value
  const reviews = list.reduce((s, p) => s + p.reviews, 0)
  const notes = list.reduce((s, p) => s + p.notesTotal, 0)
  return [
    { label: PROGRAMS_LABEL[filter.value], value: list.length.toLocaleString('fr-FR') },
    { label: 'Groupes', value: list.reduce((s, p) => s + p.binomes, 0).toLocaleString('fr-FR') },
    { label: 'Sessions réalisées', value: list.reduce((s, p) => s + p.sessions, 0).toLocaleString('fr-FR') },
    {
      label: 'Note moyenne des sessions',
      value: reviews >= MIN_REVIEWS ? `${formatNote(notes / reviews)} / 5` : '—',
    },
  ]
})
</script>
